import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import AppRouter from 'common/AppRouter';
import OnboardingCard from './OnboardingCard';
import { ONBOARDING_CARDS, DASHBOARD_CONTENT } from './onboardingConfig';
import { GitHubAppInstallationModal } from '../../modals/GitHubAppInstallationModal';
import { isGitHubAppConnectedSelector } from '../../state/selectors/sensors-authorizations';
import { getSensorAuthorizations } from '../../state/callers';
import useSettingsService from '../hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';

interface SeamlessOnboardingDashboardProps extends RouteComponentProps {
  // Add any Redux props here
}

const SeamlessOnboardingDashboard: React.FC<
  SeamlessOnboardingDashboardProps
> = ({ history }) => {
  const dispatch = useDispatch();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const [isGitHubAppModalVisible, setIsGitHubAppModalVisible] = useState(false);
  const isGitHubAppConnected = useSelector(isGitHubAppConnectedSelector);

  const isGitHubAppIntegrationEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isGitHubAppIntegrationEnabled
  });

  useEffect(() => {
    // Fetch sensor authorizations when component mounts
    // Include GitHub App based on the feature flag
    const params = {
      withGitStreamEnabled: true,
      withoutGitHubApp: !isGitHubAppIntegrationEnabled
    };
    dispatch(getSensorAuthorizations(params));
  }, [dispatch, isGitHubAppIntegrationEnabled]);

  const handleCardAction = (cardId: string) => {
    switch (cardId) {
      case 'automation':
        if (isGitHubAppConnected) {
          // Already connected, could navigate to automations page
          // For now, just don't open modal
        } else {
          setIsGitHubAppModalVisible(true);
        }
        break;
      case 'survey':
        history.push(AppRouter.SURVEYS_CREATE.path);
        break;
      default:
        break;
    }
  };

  const handleCloseGitHubAppModal = () => {
    setIsGitHubAppModalVisible(false);
  };

  return (
    <div className='flex-1 bg-gray-50 pt-12 pb-32 px-6 lg:px-12'>
      <div className='mx-auto max-w-7xl'>
        {/* Header - LEFT ALIGNED */}
        <div className='mb-8 text-left'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            {DASHBOARD_CONTENT.title}
          </h1>
          <p className='text-gray-600'>{DASHBOARD_CONTENT.subtitle}</p>
        </div>

        {/* Cards Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {ONBOARDING_CARDS.map(card => {
            // Determine button text based on connection status
            let { buttonText } = card;
            if (card.id === 'automation' && isGitHubAppConnected) {
              buttonText = 'Connected ✓';
            }

            return (
              <OnboardingCard
                key={card.id}
                id={card.id}
                title={card.title}
                description={card.description}
                additionalInfo={card.additionalInfo}
                buttonText={buttonText}
                onButtonClick={() => handleCardAction(card.id)}
                imageSrc={card.imageSrc}
                imageAlt={card.imageAlt}
                gradientClasses={card.gradientClasses}
              />
            );
          })}
        </div>
      </div>

      {/* GitHub App Installation Modal */}
      <GitHubAppInstallationModal
        visible={isGitHubAppModalVisible}
        onClose={handleCloseGitHubAppModal}
      />
    </div>
  );
};

export default SeamlessOnboardingDashboard;
